/* eslint-disable jsx-a11y/label-has-for */
/* <label> requires input present. The input is inside NumberFormat, unreadable by eslint */
import React, { isValidElement, useMemo, useState } from 'react';
import { bool, func, node, number, oneOf, oneOfType, shape, string } from 'prop-types';
import NumberFormat from 'react-number-format';

import Typography from '@/components/Typography';

import {
  styTextWrapper,
  styTextInput,
  styTextAppend,
  styTextPrepend,
  styNumberBorderBottom,
} from './style';

const InputNumber = (props) => {
  const {
    id,
    name,
    append,
    block,
    bold,
    className,
    decimalScale,
    decimalSeparator,
    defaultValue,
    disabled,
    error,
    maxValue,
    minValue,
    onAppendClick,
    onBlur,
    onChange,
    onEnter,
    onKeyDown,
    onPrependClick,
    placeholder,
    prepend,
    size,
    style,
    textHint,
    thousandSeparator,
    value: propsValue,
    variant,
    borderColor,
    ...inputProps
  } = props;

  // Store ref to element using state to trigger re-render on mount
  const [appendEl, setAppendEl] = useState(null);
  const [prependEl, setPrependEl] = useState(null);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== undefined, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = (libEvent) => {
    if (disabled) return;

    const event = { ...new Event('change') };
    const newValue = libEvent.floatValue ?? null;
    if (maxValue !== null && newValue > maxValue) {
      // If max value is provided and new value is above max value, replace with max value
      event.target = { value: maxValue };
    } else if (minValue !== null && newValue < minValue) {
      // If min value is provided and new value is below min value, replace with min value
      event.target = { value: minValue };
    } else {
      // Otherwise proceed as normal
      event.target = { value: newValue };
    }
    onChange(event);

    if (!isControlled) setStateValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onEnter();
    }
    onKeyDown(event);
  };

  const numberProps = {
    allowNegative: false,
    decimalScale,
    decimalSeparator,
    disabled,
    placeholder,
    readOnly: disabled,
    thousandSeparator,
  };

  const styProps = {
    size,
    block,
    disabled,
    bold,
    error,
    append,
    appendClick: !!onAppendClick,
    appendPadding: appendEl?.offsetWidth || 36,
    prepend,
    prependClick: !!onPrependClick,
    prependPadding: prependEl?.offsetWidth || 36,
    borderColor,
  };

  const styleByVariant = () => {
    switch (variant) {
      case 'border-bottom':
        return styNumberBorderBottom(styProps);
      case 'default':
      default:
        return styTextInput(styProps);
    }
  };

  const renderAppendPrepend = (elem, spanProps) =>
    elem && (
      <span {...spanProps}>
        {isValidElement(elem) ? (
          elem
        ) : (
          <Typography type="body" bold={bold} style={{ margin: 0 }}>
            {elem}
          </Typography>
        )}
      </span>
    );

  const renderMessage = () => {
    switch (true) {
      case !!error:
        // If error exists, show error as highest priority
        return (
          <div>
            <Typography type="alert" alertType="error" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          </div>
        );
      case !!textHint:
        // If no higher priorities present, display textHint if available
        return (
          <div>
            <Typography type="label" style={{ marginTop: 4 }}>
              {textHint}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <label htmlFor={id} className={styTextWrapper(styProps)}>
      {renderAppendPrepend(prepend, {
        className: styTextPrepend(styProps),
        ref: (v) => setPrependEl(v),
        onClick: onPrependClick,
      })}
      <NumberFormat
        id={id}
        data-testid={id}
        name={name}
        style={style}
        className={`${styleByVariant()} ${className}`}
        // Component is always controlled
        // If value is provided, use the provided value. Otherwise, use value stored on state
        // If value is null or undefined, return empty string instead,
        // as react-number-format does not accept null or undefined
        value={(isControlled ? propsValue : stateValue) ?? ''}
        // Do not pass defaultValue
        onValueChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        {...inputProps}
        {...numberProps}
      />
      {renderAppendPrepend(append, {
        className: styTextAppend(styProps),
        ref: (v) => setAppendEl(v),
        onClick: onAppendClick,
      })}
      {renderMessage()}
    </label>
  );
};

InputNumber.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  append: oneOfType([string, node]),
  block: bool,
  bold: bool,
  className: string,
  decimalScale: number,
  decimalSeparator: string,
  defaultValue: number,
  disabled: bool,
  error: string,
  maxValue: number,
  minValue: number,
  onAppendClick: func,
  onBlur: func,
  onChange: func,
  onEnter: func,
  onKeyDown: func,
  onPrependClick: func,
  placeholder: string,
  prepend: oneOfType([string, node]),
  size: oneOf(['sm', 'md']),
  style: shape({}),
  textHint: string,
  thousandSeparator: oneOfType([string, bool]),
  value: number,
  variant: oneOf(['default', 'border-bottom']),
  borderColor: string,
};

InputNumber.defaultProps = {
  append: '',
  block: false,
  bold: false,
  className: '',
  decimalScale: 0,
  decimalSeparator: ',',
  defaultValue: null,
  disabled: false,
  error: '',
  maxValue: null,
  minValue: 0,
  onAppendClick: null,
  onBlur: () => {},
  onChange: () => {},
  onEnter: () => {},
  onKeyDown: () => {},
  onPrependClick: null,
  placeholder: '',
  prepend: '',
  size: 'md',
  style: {},
  textHint: '',
  thousandSeparator: '.',
  value: undefined,
  variant: 'default',
  borderColor: '',
};

export default InputNumber;
